// Mutation hooks for Terms & Conditions Management.
// Invalidation strategy:
//   - Type CRUD → invalidate the types list (versionCount may change on delete).
//   - Version create/update → invalidate the type-scoped slice (list + details).
//   - Publish → invalidate the type-scoped slice AND the types list, because
//     it atomically flips two version statuses (DRAFT → PUBLISHED + previous
//     PUBLISHED → ARCHIVED in the same transaction; contract §3.9).
//   - Version delete → invalidate types() (versionCount changes) + versionList
//     for the type, and remove the cached single-version detail.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { termsApi } from '../api/terms.adapter';
import type {
  CreateTermTypeInput,
  CreateTermVersionInput,
  TermType,
  TermVersion,
  UpdateTermTypeInput,
  UpdateTermVersionInput,
} from '../types/terms.types';
import { termsQueryKeys } from './use-terms';

// ─── Type mutations (Root only on the wire) ───

export function useCreateTermType() {
  const qc = useQueryClient();
  return useMutation<TermType, Error, CreateTermTypeInput>({
    mutationFn: (input) => termsApi.createType(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: termsQueryKeys.types() });
    },
  });
}

export function useUpdateTermType() {
  const qc = useQueryClient();
  return useMutation<TermType, Error, { id: number; input: UpdateTermTypeInput }>({
    mutationFn: ({ id, input }) => termsApi.updateType(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: termsQueryKeys.types() });
    },
  });
}

export function useDeleteTermType() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => termsApi.deleteType(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: termsQueryKeys.types() });
      // Drop any cached versions/details for the removed type — there is no
      // recovery path; the type is gone (TYPE_HAS_VERSIONS prevents deleting
      // a non-empty type, so this is defensive cleanup).
      qc.removeQueries({ queryKey: termsQueryKeys.type(id) });
    },
  });
}

// ─── Version mutations ───

export function useCreateTermVersion() {
  const qc = useQueryClient();
  return useMutation<TermVersion, Error, { typeId: number; input: CreateTermVersionInput }>({
    mutationFn: ({ typeId, input }) => termsApi.createVersion(typeId, input),
    onSuccess: (_data, { typeId }) => {
      // versionCount on the parent type bumped by 1.
      qc.invalidateQueries({ queryKey: termsQueryKeys.types() });
      qc.invalidateQueries({ queryKey: termsQueryKeys.versionList(typeId) });
    },
  });
}

export function useUpdateTermVersion() {
  const qc = useQueryClient();
  return useMutation<
    TermVersion,
    Error,
    { typeId: number; versionId: number; input: UpdateTermVersionInput }
  >({
    mutationFn: ({ typeId, versionId, input }) =>
      termsApi.updateVersion(typeId, versionId, input),
    onSuccess: (_data, { typeId, versionId }) => {
      qc.invalidateQueries({ queryKey: termsQueryKeys.versionList(typeId) });
      qc.invalidateQueries({ queryKey: termsQueryKeys.versionDetail(typeId, versionId) });
    },
  });
}

// Publish: contract §3.9 returns 204 (per Q4) so the FE invalidates the
// type-scoped slice and re-fetches the new statuses rather than reading them
// from the response.
export function usePublishTermVersion() {
  const qc = useQueryClient();
  return useMutation<void, Error, { typeId: number; versionId: number }>({
    mutationFn: ({ typeId, versionId }) => termsApi.publishVersion(typeId, versionId),
    onSuccess: (_data, { typeId }) => {
      qc.invalidateQueries({ queryKey: termsQueryKeys.types() });
      // The type prefix flushes the version list + EVERY version detail under
      // this type, because the previously-PUBLISHED version's status changed
      // too and we don't know its id here.
      qc.invalidateQueries({ queryKey: termsQueryKeys.type(typeId) });
    },
  });
}

export function useDeleteTermVersion() {
  const qc = useQueryClient();
  return useMutation<void, Error, { typeId: number; versionId: number }>({
    mutationFn: ({ typeId, versionId }) => termsApi.deleteVersion(typeId, versionId),
    onSuccess: (_data, { typeId, versionId }) => {
      // versionCount on the parent type decremented by 1.
      qc.invalidateQueries({ queryKey: termsQueryKeys.types() });
      qc.invalidateQueries({ queryKey: termsQueryKeys.versionList(typeId) });
      qc.removeQueries({ queryKey: termsQueryKeys.versionDetail(typeId, versionId) });
    },
  });
}
